import type { Salon } from "@/data/types";
import { withBookingUtm } from "@/lib/booking";

export type CtaKind = "book" | "call" | "directions" | "salonPicker";

export interface SalonCta {
  kind: CtaKind;
  /** Final href. Empty string when disabled or when this CTA opens the picker. */
  href: string;
  /** When true, render as a disabled button (never as a link). */
  disabled: boolean;
  /** When true, the CTA has no salon context — caller should open SalonPickerSheet. */
  opensPicker: boolean;
  /** Suggested label, e.g. "Boka tid" or "Bokning öppnar snart". */
  label: string;
  /** Optional secondary label (e.g. formatted phone number). */
  subLabel?: string;
  /** External link target/rel — only when opening BokaDirekt / Maps. */
  external: boolean;
  /** Analytics event name. */
  event: "book_now_click" | "call_click" | "directions_click" | "salon_select";
}

const LABELS: Record<CtaKind, string> = {
  book: "Boka tid",
  call: "Ring salongen",
  directions: "Vägbeskrivning",
  salonPicker: "Välj salong",
};

const EVENTS: Record<CtaKind, SalonCta["event"]> = {
  book: "book_now_click",
  call: "call_click",
  directions: "directions_click",
  salonPicker: "salon_select",
};

function disabled(kind: CtaKind, label: string): SalonCta {
  return {
    kind,
    href: "",
    disabled: true,
    opensPicker: false,
    label,
    external: false,
    event: EVENTS[kind],
  };
}

function picker(kind: CtaKind): SalonCta {
  return {
    kind,
    href: "",
    disabled: false,
    opensPicker: true,
    label: LABELS[kind],
    external: false,
    event: EVENTS[kind],
  };
}

/**
 * Single source of truth for salon-bound CTAs.
 * Returns descriptors for book / call / directions / salonPicker that
 * components render uniformly via <SalonCtaButtons />.
 */
export function getSalonCtas(salon?: Salon | null): Record<CtaKind, SalonCta> {
  if (!salon) {
    return {
      book: picker("book"),
      call: picker("call"),
      directions: picker("directions"),
      salonPicker: picker("salonPicker"),
    };
  }

  const book: SalonCta = salon.bokaDirektUrl
    ? {
        kind: "book",
        href: withBookingUtm(salon.bokaDirektUrl),
        disabled: false,
        opensPicker: false,
        label: LABELS.book,
        external: true,
        event: EVENTS.book,
      }
    : disabled("book", "Bokning öppnar snart");

  const call: SalonCta = salon.phone
    ? {
        kind: "call",
        href: `tel:${salon.phone}`,
        disabled: false,
        opensPicker: false,
        label: LABELS.call,
        subLabel: salon.phoneDisplay,
        external: false,
        event: EVENTS.call,
      }
    : disabled("call", "Telefon saknas");

  const directions: SalonCta = salon.mapsUrl
    ? {
        kind: "directions",
        href: salon.mapsUrl,
        disabled: false,
        opensPicker: false,
        label: LABELS.directions,
        external: true,
        event: EVENTS.directions,
      }
    : disabled("directions", "Karta saknas");

  return {
    book,
    call,
    directions,
    salonPicker: picker("salonPicker"),
  };
}
